<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency'
import { useInboxStore } from '@/stores/useInboxStore'

const props = defineProps({
  message:    { type: Object, required: true },
  customerId: { type: Number, default: null }
})

const { t } = useI18n()
const { formatDateTime } = useCurrency()
const store = useInboxStore()

const mine = computed(() => props.message.direction === 'outgoing')
const status = computed(() => props.message.status || 'sent')

const onRetry = () => store.retryMessage(props.message.id)

const isImage = (type) => typeof type === 'string' && type.startsWith('image/')

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

const savingPassport = ref(false)
const passportSaved  = ref(false)

const canSaveAsPassport = (att) =>
  !mine.value && props.customerId && isImage(att.file_type)

const saveAsPassport = async (att) => {
  if (savingPassport.value) return
  if (!confirm(t('inbox.thread.savePassportConfirm'))) return
  savingPassport.value = true
  try {
    const res = await fetch(`/inbox/attachments/${props.message.id}/${att.idx}/save_as_passport`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
    passportSaved.value = res.ok
  } finally {
    savingPassport.value = false
  }
}
</script>

<template>
  <div class="flex" :class="mine ? 'justify-end' : 'justify-start'">
    <div
      class="max-w-[75%] px-3 py-2 text-sm transition-opacity"
      :class="[
        mine ? 'bg-[color:var(--accentSoft)] text-[color:var(--ink)]' : 'bg-[color:var(--surfaceAlt)] text-[color:var(--ink)]',
        'rounded-[var(--radius-lg)]',
        status === 'sending' ? 'opacity-60' : '',
        status === 'failed' ? 'ring-2 ring-[color:var(--danger)]' : '',
      ]"
    >
      <template v-if="message.attachments && message.attachments.length">
        <div v-for="att in message.attachments" :key="att.idx" class="mb-1">
          <a v-if="!isImage(att.file_type)" :href="att.url" target="_blank" class="underline text-[color:var(--accent)]">
            📎 {{ att.file_name || 'attachment' }}
          </a>
          <a v-else :href="att.url" target="_blank">
            <img :src="att.url" :alt="att.file_name"
                 class="rounded-[var(--radius-md)] max-w-[200px] max-h-[200px] object-cover" />
          </a>
          <div v-if="canSaveAsPassport(att)" class="mt-1">
            <button
              v-if="!passportSaved"
              type="button"
              class="text-[10px] underline text-[color:var(--accent)] disabled:opacity-50"
              :disabled="savingPassport"
              @click="saveAsPassport(att)"
            >
              {{ savingPassport ? t('inbox.thread.savingPassport') : t('inbox.thread.saveAsPassport') }}
            </button>
            <span v-else class="text-[10px] text-[color:var(--ok)]">
              ✓ {{ t('inbox.thread.passportSaved') }}
            </span>
          </div>
        </div>
      </template>
      <p class="whitespace-pre-wrap leading-relaxed" v-if="message.content">{{ message.content }}</p>
      <div class="mt-1 text-[10px] text-[color:var(--muted)] flex items-center gap-1 fw-value">
        <span>{{ formatDateTime(message.sent_at) }}</span>
        <span v-if="mine && message.sent_by_user_name">· {{ message.sent_by_user_name }} · {{ t('inbox.thread.fromCrm') }}</span>
        <span v-else-if="mine && message.external_sender">· {{ message.external_sender }} · {{ t('inbox.thread.externalAgent') }}</span>
        <span v-if="status === 'sending'" class="ml-1 inline-flex items-center gap-1">
          · <span class="inline-block w-1.5 h-1.5 rounded-full animate-pulse" :style="{ background: 'var(--muted)' }" />
        </span>
        <template v-if="status === 'failed'">
          <span class="ml-1 text-[color:var(--danger)]">· {{ t('inbox.thread.failed') }}</span>
          <button
            type="button"
            class="ml-1 underline text-[color:var(--accent)]"
            @click="onRetry"
          >{{ t('inbox.thread.retry') }}</button>
        </template>
      </div>
    </div>
  </div>
</template>
